import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogIn, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Login successful');
      navigate('/dashboard');
    } else {
      // Set specific error message based on error code
      if (result.errorCode === 'EMAIL_NOT_FOUND') {
        setErrorMessage('Please provide correct Email');
      } else if (result.errorCode === 'WRONG_PASSWORD') {
        setErrorMessage('Please contact System Admin to reset your password');
      } else {
        setErrorMessage('Please contact System Admin for appropriate credentials');
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-gradient-to-br from-gray-100 via-white to-gray-50" data-testid="login-page">
      {/* Left side - Form */}
      <div className="flex flex-col items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md space-y-8 p-8 rounded-2xl glass-card">
          <div className="space-y-3 text-center">
            <div className="mb-4">
              <img 
                src="/rc-logo.png" 
                alt="Radiants Civiletric Logo" 
                className="h-24 w-auto mx-auto"
                style={{ imageRendering: 'crisp-edges' }}
              />
            </div>
            <h1 className="text-5xl font-bold tracking-tight text-[#DC143C]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome Back
            </h1>
            <p className="text-gray-600 text-base">
              Sign in to Radiants Civiletric Portal
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
                className="glass-input"
                className="h-12 border-2 focus:border-[#DC143C] focus:ring-[#DC143C]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-semibold">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
                className="h-12 border-2 focus:border-[#DC143C] focus:ring-[#DC143C]"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-[#DC143C] to-[#b91130] hover:from-[#b91130] hover:to-[#8b0e25] text-white text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? (
                <span>Signing in...</span>
              ) : (
                <span>Sign In</span>
              )}
            </Button>
            
            {/* Error Message Display */}
            {errorMessage && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="login-error-message">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600 font-medium">{errorMessage}</p>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Right side - Image */}
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1750364567724-b3c7cf0642a7?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(27, 39, 61, 0.95), rgba(220, 20, 60, 0.85))'
          }}
        >
          <div className="flex items-center justify-center h-full p-12">
            <div className="text-white space-y-6 max-w-lg">
              <h2 className="text-5xl font-bold leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Radiants Civiletric
              </h2>
              <p className="text-2xl text-gray-100 font-medium italic">
                Let The Spark Begins
              </p>
              <p className="text-lg text-gray-200 leading-relaxed">
                Streamline project management, approvals, and billing workflows. Building great things with value & quality.
              </p>
              <div className="flex gap-4 pt-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span className="text-sm text-gray-100">Civil Services</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span className="text-sm text-gray-100">Electrical</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span className="text-sm text-gray-100">Fire Fighting</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
